import React, { useState, useEffect, useContext } from 'react'
import '../styles/MarketsV2.css'
import MarketSearchBar from './MarketSearchBar'
import { keyIndicatorTimeOptions } from '../helper/FeedHelper'
import { mobileMarketWatchOptions } from '../helper/FeedHelper'
import { AuthContext } from '../context/AuthContext'
import axios from 'axios'
import config from '../config'
import { useNavigate } from 'react-router-dom'
import { ThemeContext } from '../context/ThemeContext'
import {
  formatNumber,
  getChangePercentageColor,
  handleNavigateToAsset,
  forexRegions,
} from '../helper/MarketsHelper'
import {
  formatCurrency as fc,
  LoadingSpinner,
  formatModifiedTicker,
} from '../helper/StylesHelper'
import useResponsiveWidth from '../hooks/useResponsiveWidth'
import { Sparklines, SparklinesLine } from 'react-sparklines'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons'

export default function MarketsV2() {
  useResponsiveWidth()
  const navigate = useNavigate()

  const { isAuthenticated, userToken } = useContext(AuthContext)
  const { tc } = useContext(ThemeContext)

  //
  // ---------------- STATE: KEY INDICATORS ----------------
  //
  const [keyIndicatorData, setKeyIndicatorData] = useState([])
  const [keyIndicatorsLoading, setKeyIndicatorsLoading] = useState(false)
  const [activeIndicatorIndex, setActiveIndicatorIndex] = useState(0)
  const [keyIndicatorTime, setKeyIndicatorTime] = useState('1d')

  //
  // ---------------- STATE: WORLD MARKETS ----------------
  //
  const [worldMarketsData, setWorldMarketsData] = useState([])
  const [worldMarketsLoading, setWorldMarketsLoading] = useState(false)
  const [selectedWorldMarketsType, setSelectedWorldMarketsType] = useState('us') // 'us' or 'commodities'
  const [worldMarketsPage, setWorldMarketsPage] = useState(1)
  const [worldMarketsTotalPages, setWorldMarketsTotalPages] = useState(0)

  //
  // ---------------- STATE: FOREX ----------------
  //
  const [forexData, setForexData] = useState([])
  const [forexLoading, setForexLoading] = useState(false)
  const [forexPage, setForexPage] = useState(1)
  const [forexTotalPages, setForexTotalPages] = useState(0)
  const [selectedForexRegion, setSelectedForexRegion] = useState(
    forexRegions[0]
  )

  //
  // ---------------- STATE: STOCKS SECTION (WINNERS/LOSERS/MOST ACTIVE/MARKETCAP) ----------------
  //
  const [stocksMode, setStocksMode] = useState('winners')
  const [stocksPage, setStocksPage] = useState(1)
  const [stocksData, setStocksData] = useState([])
  const [stocksTotalPages, setStocksTotalPages] = useState(1)
  const [stocksLoading, setStocksLoading] = useState(false)

  //
  // ---------------- STATE: CRYPTO SECTION (WINNERS/LOSERS/MOST ACTIVE/MARKETCAP) ----------------
  //
  const [cryptoMode, setCryptoMode] = useState('winners')
  const [cryptoPage, setCryptoPage] = useState(1)
  const [cryptoData, setCryptoData] = useState([])
  const [cryptoTotalPages, setCryptoTotalPages] = useState(1)
  const [cryptoLoading, setCryptoLoading] = useState(false)

  //
  // ---------------- STATE: TRENDING ON VESTR (STOCKS vs CRYPTO) ----------------
  //
  const [trendingVestrMode, setTrendingVestrMode] = useState('stocks') // 'stocks' or 'crypto'
  const [trendingVestrPage, setTrendingVestrPage] = useState(1)
  const [trendingVestrData, setTrendingVestrData] = useState([])
  const [trendingVestrTotalPages, setTrendingVestrTotalPages] = useState(1)
  const [trendingVestrLoading, setTrendingVestrLoading] = useState(false)

  //
  // ---------------- STATE: TOPLIST (CHARTS TAB) ----------------
  //
  const [activeTopType, setActiveTopType] = useState('stocks')
  const [activeUsFilter, setActiveTopFilter] = useState('mostactive')
  const [activeTopData, setActiveTopData] = useState([])
  const [activeTopLoading, setActiveTopLoading] = useState(false)
  const [activeTopPage, setActiveTopPage] = useState(1)

  //
  // ---------------- STATE: WATCHLISTS ----------------
  //
  const [stockLists, setStockLists] = useState([])
  const [stockListsLoading, setStockListsLoading] = useState(false)
  const [activeWatchlist, setActiveWatchlist] = useState(null)
  const [activeListStocks, setActiveListStocks] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [isCreatingNewList, setIsCreatingNewList] = useState(false)
  const [newListName, setNewListName] = useState('')
  const [sortOrder, setSortOrder] = useState('desc')

  //
  // ---------------- SEARCH ----------------
  //
  const [stock, setStock] = useState(null)

  //
  // ---------------- EXPLORE TAB ----------------
  //
  const [selectedExploreTab, setSelectedExploreTab] = useState('overview')
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [selectedSection, setSelectedSection] = useState('Indices')
  //
  // =============== API FETCH FUNCTIONS ===============
  //

  // Key Indicators
  const fetchKeyIndicators = async () => {
    setKeyIndicatorsLoading(true)
    try {
      const response = await axios.get(
        `${config.backendUrl}/api/markets/keyindicators`,
        {
          headers: { Authorization: `Bearer ${userToken}` },
          params: {
            includeDayData: true,
            filter: keyIndicatorTime,
          },
        }
      )
      setKeyIndicatorData(response.data)
    } catch (error) {
      console.error('Error fetching key indicators:', error)
    }
    setKeyIndicatorsLoading(false)
  }

  // World Markets
  const fetchWorldMarkets = async () => {
    setWorldMarketsLoading(true)
    try {
      if (selectedWorldMarketsType === 'commodities') {
        const response = await axios.get(
          `${config.backendUrl}/api/markets/forexAndCommodities`,
          {
            params: {
              includeDayData: true,
              page: worldMarketsPage,
              assetType: 'commodities',
            },
          }
        )
        setWorldMarketsData(response.data.data)
        setWorldMarketsTotalPages(response.data.totalPages)
      } else {
        const response = await axios.get(
          `${config.backendUrl}/api/markets/worldMarkets`,
          {
            params: {
              includeDayData: true,
              page: worldMarketsPage,
              type: 'us',
            },
          }
        )
        setWorldMarketsData(response.data.data)
        setWorldMarketsTotalPages(response.data.totalPages)
      }
    } catch (error) {
      console.error('Error fetching world markets:', error)
    }
    setWorldMarketsLoading(false)
  }

  // Forex
  const fetchForex = async () => {
    setForexLoading(true)
    try {
      const response = await axios.get(
        `${config.backendUrl}/api/markets/forexAndCommodities`,
        {
          params: {
            includeDayData: true,
            page: forexPage,
            assetType: 'forex',
            forexRegion: selectedForexRegion,
          },
        }
      )
      setForexData(response.data.data)
      setForexTotalPages(response.data.totalPages)
    } catch (error) {
      console.error('Error fetching forex:', error)
    }
    setForexLoading(false)
  }

  // Stocks
  // Now using /api/markets/topmktcap for 'marketcap' instead of /toplist
  const fetchStocksData = async () => {
    setStocksLoading(true)
    try {
      let url = ''
      let params = { includeDayData: true, page: stocksPage, itemsPerPage: 7 }

      if (stocksMode === 'winners') {
        url = `${config.backendUrl}/api/markets/gainerslosers`
        params.assetType = 'stocks'
        params.type = 'gainers'
      } else if (stocksMode === 'losers') {
        url = `${config.backendUrl}/api/markets/gainerslosers`
        params.assetType = 'stocks'
        params.type = 'losers'
      } else if (stocksMode === 'mostactive') {
        url = `${config.backendUrl}/api/markets/mostactive`
        params.assetType = 'stocks'
      } else {
        // 'marketcap' => use /api/markets/topmktcap for stocks
        url = `${config.backendUrl}/api/markets/topmktcap`
        params.assetType = 'stocks'
      }

      const response = await axios.get(url, { params })
      // Response shape matches /mostactive & /gainerslosers:
      // { data: [...], averageChange, page, totalPages }
      const { data, totalPages } = response.data
      setStocksData(data)
      setStocksTotalPages(totalPages)
    } catch (error) {
      console.error('Error fetching stocks data:', error)
    }
    setStocksLoading(false)
  }

  // Crypto
  // Now using /api/markets/topmktcap for 'marketcap' instead of /toplist
  const fetchCryptoData = async () => {
    setCryptoLoading(true)
    try {
      let url = ''
      let params = { includeDayData: true, page: cryptoPage, itemsPerPage: 7 }

      if (cryptoMode === 'winners') {
        url = `${config.backendUrl}/api/markets/gainerslosers`
        params.assetType = 'crypto'
        params.type = 'gainers'
      } else if (cryptoMode === 'losers') {
        url = `${config.backendUrl}/api/markets/gainerslosers`
        params.assetType = 'crypto'
        params.type = 'losers'
      } else if (cryptoMode === 'mostactive') {
        url = `${config.backendUrl}/api/markets/mostactive`
        params.assetType = 'crypto'
      } else {
        // 'marketcap' => use /api/markets/topmktcap for crypto
        url = `${config.backendUrl}/api/markets/topmktcap`
        params.assetType = 'crypto'
      }

      const response = await axios.get(url, { params })
      const { data, totalPages } = response.data
      setCryptoData(data)
      setCryptoTotalPages(totalPages)
    } catch (error) {
      console.error('Error fetching crypto data:', error)
    }
    setCryptoLoading(false)
  }

  // Trending on Vestr (Stocks or Crypto)
  const fetchTrendingOnVestrData = async () => {
    setTrendingVestrLoading(true)
    try {
      const url = `${config.backendUrl}/api/markets/mostactiveonvestr`
      const params = {
        assetType: trendingVestrMode === 'stocks' ? 'stocks' : 'crypto',
        includeDayData: true,
        page: trendingVestrPage,
      }

      const response = await axios.get(url, { params })
      setTrendingVestrData(response.data.data)
      setTrendingVestrTotalPages(response.data.totalPages)
    } catch (error) {
      console.error('Error fetching trending on Vestr data:', error)
    }
    setTrendingVestrLoading(false)
  }

  // Toplist (Charts Tab)
  const fetchToplistData = async () => {
    setActiveTopLoading(true)
    try {
      const response = await axios.get(
        `${config.backendUrl}/api/markets/toplist`,
        {
          params: {
            assetType: activeTopType,
            type: activeUsFilter,
            page: activeTopPage,
          },
        }
      )
      setActiveTopData(response.data)
    } catch (error) {
      console.error('Error fetching toplist data:', error.message)
    }
    setActiveTopLoading(false)
  }

  // Watchlists (user created)
  const fetchStockLists = async (so = 'desc') => {
    setStockListsLoading(true)
    if (!isAuthenticated) {
      setStockListsLoading(false)
      return
    }
    try {
      const response = await axios.get(
        `${config.backendUrl}/api/markets/stocklists`,
        {
          headers: { Authorization: `Bearer ${userToken}` },
          params: {
            sortOrder: so, // 'asc' or 'desc'
            includeStocks: false,
          },
        }
      )
      setStockLists(response.data)
      if (response.data[0]) {
        setActiveWatchlist(response.data[0]._id)
      }
    } catch (error) {
      console.error('Error fetching stock lists:', error)
    }
    setStockListsLoading(false)
  }

  const fetchActiveListStocks = async () => {
    setStockListsLoading(true)
    if (!activeWatchlist) {
      setStockListsLoading(false)
      return
    }
    try {
      const response = await axios.get(
        `${config.backendUrl}/api/markets/stocklists`,
        {
          headers: { Authorization: `Bearer ${userToken}` },
          params: {
            listIds: activeWatchlist,
            page: currentPage,
            includeDayData: true,
          },
        }
      )
      if (response.data.length > 0) {
        const activeList = response.data[0]
        setActiveListStocks(activeList.stocks)
        setTotalPages(activeList.totalPages)
      } else {
        setActiveListStocks([])
        setTotalPages(1)
      }
    } catch (error) {
      console.error('Error fetching active list stocks:', error)
    }
    setStockListsLoading(false)
  }

  //
  // =============== HANDLERS ===============
  //
  const handleSortToggle = () => {
    const newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc'
    setSortOrder(newSortOrder)
    fetchStockLists(newSortOrder)
  }

  const handleCreateNewClick = () => {
    if (!isAuthenticated) {
      alert('Please sign in to access this feature')
      return
    }
    setIsCreatingNewList(true)
  }

  const handleCreateList = async () => {
    if (!isAuthenticated) {
      alert('Please sign in to access this feature')
      return
    }
    if (!newListName.trim()) {
      alert('Please enter a list name')
      return
    }
    try {
      const response = await axios.post(
        `${config.backendUrl}/api/markets/stocklists`,
        { name: newListName },
        {
          headers: { Authorization: `Bearer ${userToken}` },
        }
      )
      setStockLists([...stockLists, response.data])
      setNewListName('')
      setIsCreatingNewList(false)
    } catch (error) {
      alert(error.response?.data?.error || 'Error creating new stock list')
      console.error('Error creating new stock list:', error)
    }
  }

  const handleDeleteList = async (listId) => {
    if (!window.confirm('Are you sure you want to delete this stock list?')) {
      return
    }
    try {
      await axios.delete(
        `${config.backendUrl}/api/markets/stocklists/${listId}`,
        {
          headers: { Authorization: `Bearer ${userToken}` },
        }
      )
      setStockLists((prev) => prev.filter((list) => list?._id !== listId))
    } catch (error) {
      console.error('Error deleting stock list:', error)
    }
  }

  //
  // =============== RENDER HELPERS ===============
  //
  const CustomReferenceLine = ({ data, value, style }) => {
    if (!data || data.length === 0) return null

    let max = Math.max(...data)
    let min = Math.min(...data)

    max = Math.max(max, value)
    min = Math.min(min, value)

    const percentY = (value - min) / (max - min)
    const y = (1 - percentY) * 100
    return <line x1="0" x2="100%" y1={`${y}%`} y2={`${y}%`} style={style} />
  }

  const renderStockItem = (stock) => {
    if (!stock) return null

    const currentPrice =
      stock.dailyChangeData?.currentPrice ?? stock.currentPrice ?? 0
    const todaysPercentChange =
      stock.dailyChangeData?.todaysPercentChange ??
      stock.todaysPercentChange ??
      0

    const closingPrices = stock.oneDayData
      ? stock.oneDayData.map((d) => d.c)
      : []

    const chartColor =
      todaysPercentChange >= 0
        ? 'var(--stock-change-pos)'
        : 'var(--stock-change-neg)'

    return (
      <div
        className="mv2-stock-item"
        key={stock.ticker}
        onClick={() => handleNavigateToAsset(navigate, stock.ticker)}
      >
        <div className="mv2-stock-item-col">
          <span className="mv2-stock-item-span bold">
            {formatModifiedTicker(stock.ticker)}
          </span>
          <span
            className="mv2-stock-item-span"
            style={{ color: 'var(--action-grey)', whiteSpace: 'nowrap' }}
          >
            {stock.companyName?.length > 15
              ? `${stock.companyName.slice(0, 15)}...`
              : stock.companyName ?? ''}
          </span>
        </div>

        <div className="mv2-stockitem-graph">
          {closingPrices.length > 0 ? (
            <Sparklines data={closingPrices} width={100} height={50} margin={5}>
              <SparklinesLine
                color={chartColor}
                style={{ strokeWidth: 1, fill: 'none' }}
                spotRadius={0}
              />
              <CustomReferenceLine
                data={closingPrices}
                value={stock.prevClosePrice || 0}
                style={{
                  stroke: 'var(--action-grey)',
                  strokeDasharray: '2, 2',
                  strokeWidth: 1,
                }}
              />
            </Sparklines>
          ) : (
            <span style={{ color: 'var(--action-grey)', textAlign: 'center' }}>
              No chart
            </span>
          )}
        </div>

        <div className="mv2-stock-item-col">
          <span
            className="mv2-stock-item-span bold"
            style={{ display: 'flex', justifyContent: 'flex-end' }}
          >
            ${fc(currentPrice, 2)}
          </span>
          <span
            className="mv2-stock-item-span bold"
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              color: getChangePercentageColor(todaysPercentChange, tc()),
            }}
          >
            {todaysPercentChange >= 0 ? '↑' : '↓'}
            {fc(todaysPercentChange, 2, true)}%
          </span>
        </div>
      </div>
    )
  }

  const renderStockCard = (stock) => {
    if (!stock) return null

    const closingPrices = stock.oneDayData?.map((d) => d.c) || []
    const chartColor =
      stock.todaysPercentChange >= 0
        ? 'var(--stock-change-pos)'
        : 'var(--stock-change-neg)'

    return (
      <div
        className="mv2-stock-card"
        key={stock.ticker}
        onClick={() => handleNavigateToAsset(navigate, stock.ticker)}
      >
        <div className="mv2-stock-card-header">
          <span className="bold">{formatModifiedTicker(stock.ticker)}</span>
          <span
            className="mv2-companyName"
            style={{
              color: 'var(--action-grey)',
              height: '3rem',
              overflow: 'visible',
            }}
          >
            {stock.companyName?.length > 40
              ? `${stock.companyName.slice(0, 40)}...`
              : stock.companyName}
          </span>
        </div>
        <div className="mv2-stock-card-graph" style={{ padding: '1rem 0' }}>
          <Sparklines data={closingPrices} width={100} height={35} margin={5}>
            <SparklinesLine
              color={chartColor}
              style={{ strokeWidth: 1, fill: 'none' }}
              spotRadius={0}
            />
            <CustomReferenceLine
              data={closingPrices}
              value={stock.prevClosePrice}
              style={{
                stroke: 'var(--action-grey)',
                strokeDasharray: '2, 2',
                strokeWidth: 1,
              }}
            />
          </Sparklines>
        </div>

        <div className="mv2-stock-card-footer">
          <span className="bold">
            $
            {stock.todaysClosePrice > 0
              ? fc(stock.todaysClosePrice)
              : fc(stock.currentPrice)}
          </span>
          <span
            className={`bold ${
              stock.todaysPercentChange >= 0 ? 'ma-positive' : 'ma-negative'
            }`}
          >
            {stock.todaysPercentChange >= 0 ? '↑' : '↓'}
            {fc(Math.abs(parseFloat(stock.todaysPercentChange)), 2)}%
          </span>
        </div>
      </div>
    )
  }

  //
  // =============== RENDER SECTIONS ===============
  //

  // Key Indicators
  const renderKeyIndicators = () => {
    if (keyIndicatorsLoading) {
      return (
        <div className="mv2-keyindicators">
          <h2 className="mv2-watchlists-h2">Key Indicators</h2>
          <LoadingSpinner />
        </div>
      )
    }

    const dotMap = {
      'I:SPX': 'blue',
      'I:DJI': 'green',
      'I:NDX': 'red',
      'I:RUT': 'orange',
      'I:VIX': 'purple',
      'I:VXN': 'pink',
      'X:BTCUSD': 'gold',
      'X:ETHUSD': 'silver',
      'X:SOLUSD': 'teal',
    }

    const activeIndicator = keyIndicatorData[activeIndicatorIndex]
    if (!activeIndicator || !activeIndicator.oneDayData) return null

    const { filterPrevClosePrice, ticker } = activeIndicator
    const lineColor = dotMap[ticker] || 'white'
    const posOrNegColor = getChangePercentageColor(
      activeIndicator.filterPercentChange,
      tc()
    )
    const closingPrices = activeIndicator.oneDayData?.map((p) => p.c) || []
    const globalMin = Math.min(...closingPrices)
    const globalMax = Math.max(...closingPrices)

    return (
      <div className="mv2-keyindicators">
        <h2 className="mv2-watchlists-h2">Key Indicators</h2>
        {!isMobile && (
          <div className="mv2-section-filters-wrapper">
            <div className="mv2-section-filters">
              {keyIndicatorTimeOptions.map((time) => (
                <button
                  key={time.value}
                  className={`CreatePostFeedButton ${
                    keyIndicatorTime === time.value ? 'greenButton' : ''
                  }`}
                  onClick={() => setKeyIndicatorTime(time.value)}
                >
                  {time.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Indicator small cards */}
        <div className="mv2-keyindicators-grid">
          {keyIndicatorData.map((indicator, index) => {
            const { name, currentPrice, filterPercentChange, ticker } =
              indicator
            const changeColor = getChangePercentageColor(
              filterPercentChange,
              tc()
            )

            return (
              <div
                className={`mv2-indicator-wrapper ${
                  index === activeIndicatorIndex ? 'selected' : ''
                }`}
                key={index}
                onClick={() => setActiveIndicatorIndex(index)}
                style={{
                  cursor: 'pointer',
                  border: `0.05rem solid ${
                    index === activeIndicatorIndex
                      ? 'var(--table-hover-bg)'
                      : 'var(--background-color)'
                  }`,
                }}
              >
                <div className="mv2-indicator-card">
                  <div className="mv2-indicator-header">
                    <span className="mv2-indicator-name">{name}</span>
                  </div>
                  <div className="mv2-indicator-value bold">
                    ${fc(currentPrice, 2, true)}
                  </div>
                  <div
                    className="mv2-indicator-change bold"
                    style={{ color: changeColor }}
                  >
                    {filterPercentChange >= 0 ? '↑' : '↓'}
                    {fc(filterPercentChange, 2, true)}%
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Big chart for selected indicator */}
        <div style={{ position: 'relative', marginTop: '2rem' }}>
          <Sparklines
            data={closingPrices}
            width={500}
            height={300}
            min={globalMin}
            max={globalMax}
            margin={5}
          >
            <SparklinesLine
              color={posOrNegColor}
              style={{ strokeWidth: 2, fill: 'none' }}
              spotRadius={0}
            />

            <CustomReferenceLine
              data={closingPrices}
              value={filterPrevClosePrice}
              style={{
                stroke: 'var(--action-grey)',
                strokeDasharray: '2, 2',
                strokeWidth: 1,
              }}
            />
          </Sparklines>
        </div>
      </div>
    )
  }

  // Watchlist items
  const renderActiveWatchListItems = () => {
    if (stockListsLoading) return <LoadingSpinner />
    if (!activeWatchlist) return null
    if (activeListStocks.length === 0)
      return <p>No stocks in this watchlist.</p>

    return (
      <div className="mv2-stock-cards-container">
        {activeListStocks.map((stock) => renderStockCard(stock))}
      </div>
    )
  }

  // Forex
  const renderForexSection = () => {
    return (
      <div className="mv2-keyindicators">
        <h2 className="mv2-watchlists-h2">Forex</h2>
        <div className="mv2-section-filters-wrapper">
          <div className="mv2-section-filters" style={{ flexWrap: 'wrap' }}>
            {forexRegions.map((region) => (
              <button
                key={region}
                onClick={() => {
                  setSelectedForexRegion(region)
                  setForexPage(1)
                }}
                className={`CreatePostFeedButton ${
                  selectedForexRegion === region && 'greenButton'
                }`}
              >
                {region}
              </button>
            ))}
          </div>
          <div className="mv2-pagination-arrows">
            <button
              className="mv2-pagination-button"
              onClick={() => setForexPage((prev) => Math.max(prev - 1, 1))}
              disabled={forexPage === 1}
            >
              <FontAwesomeIcon
                icon={faArrowLeft}
                style={{
                  fontSize: '2rem',
                  color:
                    forexPage === 1
                      ? 'var(--border-color)'
                      : 'var(--text-color)',
                }}
              />
            </button>
            <button
              className="mv2-pagination-button"
              onClick={() =>
                setForexPage((prev) => Math.min(prev + 1, forexTotalPages))
              }
              disabled={forexPage === forexTotalPages}
            >
              <FontAwesomeIcon
                icon={faArrowRight}
                style={{
                  fontSize: '2rem',
                  color:
                    forexPage === forexTotalPages
                      ? 'var(--border-color)'
                      : 'var(--text-color)',
                }}
              />
            </button>
          </div>
        </div>
        {forexLoading ? (
          <LoadingSpinner />
        ) : (
          forexData.map((stock) => renderStockItem(stock))
        )}
      </div>
    )
  }

  // World Markets
  const renderWorldMarketsSection = () => {
    return (
      <div className="mv2-keyindicators">
        <h2 className="mv2-watchlists-h2">US Indexes</h2>
        <div className="mv2-section-filters-wrapper">
          <div className="mv2-section-filters">
            <button
              onClick={() => {
                setSelectedWorldMarketsType('us')
                setWorldMarketsPage(1)
              }}
              className={`CreatePostFeedButton ${
                selectedWorldMarketsType === 'us' && 'greenButton'
              }`}
            >
              Major
            </button>
            <button
              onClick={() => {
                setSelectedWorldMarketsType('commodities')
                setWorldMarketsPage(1)
              }}
              className={`CreatePostFeedButton ${
                selectedWorldMarketsType === 'commodities' && 'greenButton'
              }`}
            >
              Commodities
            </button>
          </div>
          <div className="mv2-pagination-arrows">
            <button
              className="mv2-pagination-button"
              onClick={() =>
                setWorldMarketsPage((prev) => Math.max(prev - 1, 1))
              }
              disabled={worldMarketsPage === 1}
            >
              <FontAwesomeIcon
                icon={faArrowLeft}
                style={{
                  fontSize: '2rem',
                  color:
                    worldMarketsPage === 1
                      ? 'var(--border-color)'
                      : 'var(--text-color)',
                }}
              />
            </button>
            <button
              className="mv2-pagination-button"
              onClick={() =>
                setWorldMarketsPage((prev) =>
                  Math.min(prev + 1, worldMarketsTotalPages)
                )
              }
              disabled={worldMarketsPage === worldMarketsTotalPages}
            >
              <FontAwesomeIcon
                icon={faArrowRight}
                style={{
                  fontSize: '2rem',
                  color:
                    worldMarketsPage === worldMarketsTotalPages
                      ? 'var(--border-color)'
                      : 'var(--text-color)',
                }}
              />
            </button>
          </div>
        </div>
        {worldMarketsLoading ? (
          <LoadingSpinner />
        ) : (
          worldMarketsData.map((stock) => renderStockItem(stock))
        )}
      </div>
    )
  }

  // ------------------ STOCKS SECTION ------------------
  const renderStocksSection = () => {
    return (
      <div className="mv2-keyindicators">
        <h2 className="mv2-watchlists-h2">Stocks</h2>
        <div className="mv2-section-filters-wrapper">
          <div className="mv2-section-filters">
            <button
              onClick={() => {
                setStocksMode('winners')
                setStocksPage(1)
              }}
              className={`CreatePostFeedButton ${
                stocksMode === 'winners' && 'greenButton'
              }`}
            >
              Winners
            </button>
            <button
              onClick={() => {
                setStocksMode('losers')
                setStocksPage(1)
              }}
              className={`CreatePostFeedButton ${
                stocksMode === 'losers' && 'greenButton'
              }`}
            >
              Losers
            </button>
            <button
              onClick={() => {
                setStocksMode('mostactive')
                setStocksPage(1)
              }}
              className={`CreatePostFeedButton ${
                stocksMode === 'mostactive' && 'greenButton'
              }`}
            >
              Most Active
            </button>
            <button
              onClick={() => {
                setStocksMode('marketcap')
                setStocksPage(1)
              }}
              className={`CreatePostFeedButton ${
                stocksMode === 'marketcap' && 'greenButton'
              }`}
            >
              Mkt Cap
            </button>
          </div>
          <div className="mv2-pagination-arrows">
            <button
              className="mv2-pagination-button"
              onClick={() => setStocksPage((prev) => Math.max(prev - 1, 1))}
              disabled={stocksPage === 1}
            >
              <FontAwesomeIcon
                icon={faArrowLeft}
                style={{
                  fontSize: '2rem',
                  color:
                    stocksPage === 1
                      ? 'var(--border-color)'
                      : 'var(--text-color)',
                }}
              />
            </button>
            <button
              className="mv2-pagination-button"
              onClick={() =>
                setStocksPage((prev) => Math.min(prev + 1, stocksTotalPages))
              }
              disabled={stocksPage === stocksTotalPages}
            >
              <FontAwesomeIcon
                icon={faArrowRight}
                style={{
                  fontSize: '2rem',
                  color:
                    stocksPage === stocksTotalPages
                      ? 'var(--border-color)'
                      : 'var(--text-color)',
                }}
              />
            </button>
          </div>
        </div>
        {stocksLoading ? (
          <LoadingSpinner />
        ) : stocksData.length === 0 ? (
          <p>No data found.</p>
        ) : (
          stocksData.map((item) => renderStockItem(item))
        )}
      </div>
    )
  }

  // ------------------ CRYPTO SECTION ------------------
  const renderCryptoSection = () => {
    return (
      <div className="mv2-keyindicators">
        <h2 className="mv2-watchlists-h2">Crypto</h2>
        <div className="mv2-section-filters-wrapper">
          <div className="mv2-section-filters">
            <button
              onClick={() => {
                setCryptoMode('winners')
                setCryptoPage(1)
              }}
              className={`CreatePostFeedButton ${
                cryptoMode === 'winners' && 'greenButton'
              }`}
            >
              Winners
            </button>
            <button
              onClick={() => {
                setCryptoMode('losers')
                setCryptoPage(1)
              }}
              className={`CreatePostFeedButton ${
                cryptoMode === 'losers' && 'greenButton'
              }`}
            >
              Losers
            </button>
            <button
              onClick={() => {
                setCryptoMode('mostactive')
                setCryptoPage(1)
              }}
              className={`CreatePostFeedButton ${
                cryptoMode === 'mostactive' && 'greenButton'
              }`}
            >
              Most Active
            </button>
            <button
              onClick={() => {
                setCryptoMode('marketcap')
                setCryptoPage(1)
              }}
              className={`CreatePostFeedButton ${
                cryptoMode === 'marketcap' && 'greenButton'
              }`}
            >
              Mkt Cap
            </button>
          </div>
          <div className="mv2-pagination-arrows">
            <button
              className="mv2-pagination-button"
              onClick={() => setCryptoPage((prev) => Math.max(prev - 1, 1))}
              disabled={cryptoPage === 1}
            >
              <FontAwesomeIcon
                icon={faArrowLeft}
                style={{
                  fontSize: '2rem',
                  color:
                    cryptoPage === 1
                      ? 'var(--border-color)'
                      : 'var(--text-color)',
                }}
              />
            </button>
            <button
              className="mv2-pagination-button"
              onClick={() =>
                setCryptoPage((prev) => Math.min(prev + 1, cryptoTotalPages))
              }
              disabled={cryptoPage === cryptoTotalPages}
            >
              <FontAwesomeIcon
                icon={faArrowRight}
                style={{
                  fontSize: '2rem',
                  color:
                    cryptoPage === cryptoTotalPages
                      ? 'var(--border-color)'
                      : 'var(--text-color)',
                }}
              />
            </button>
          </div>
        </div>
        {cryptoLoading ? (
          <LoadingSpinner />
        ) : cryptoData.length === 0 ? (
          <p>No data found.</p>
        ) : (
          cryptoData.map((item) => renderStockItem(item))
        )}
      </div>
    )
  }

  //
  // Trending on Vestr (Stocks | Crypto)
  //
  const renderTrendingOnVestrSection = () => {
    return (
      <div className="mv2-keyindicators">
        <h2 className="mv2-watchlists-h2">Trending on Vestr</h2>
        <div className="mv2-section-filters-wrapper">
          <div className="mv2-section-filters">
            <button
              onClick={() => {
                setTrendingVestrMode('stocks')
                setTrendingVestrPage(1)
              }}
              className={`CreatePostFeedButton ${
                trendingVestrMode === 'stocks' && 'greenButton'
              }`}
            >
              Stocks
            </button>
            <button
              onClick={() => {
                setTrendingVestrMode('crypto')
                setTrendingVestrPage(1)
              }}
              className={`CreatePostFeedButton ${
                trendingVestrMode === 'crypto' && 'greenButton'
              }`}
            >
              Crypto
            </button>
          </div>
          <div className="mv2-pagination-arrows">
            <button
              className="mv2-pagination-button"
              onClick={() =>
                setTrendingVestrPage((prev) => Math.max(prev - 1, 1))
              }
              disabled={trendingVestrPage === 1}
            >
              <FontAwesomeIcon
                icon={faArrowLeft}
                style={{
                  fontSize: '2rem',
                  color:
                    trendingVestrPage === 1
                      ? 'var(--border-color)'
                      : 'var(--text-color)',
                }}
              />
            </button>
            <button
              className="mv2-pagination-button"
              onClick={() =>
                setTrendingVestrPage((prev) =>
                  Math.min(prev + 1, trendingVestrTotalPages)
                )
              }
              disabled={trendingVestrPage === trendingVestrTotalPages}
            >
              <FontAwesomeIcon
                icon={faArrowRight}
                style={{
                  fontSize: '2rem',
                  color:
                    trendingVestrPage === trendingVestrTotalPages
                      ? 'var(--border-color)'
                      : 'var(--text-color)',
                }}
              />
            </button>
          </div>
        </div>
        {trendingVestrLoading ? (
          <LoadingSpinner />
        ) : trendingVestrData.length === 0 ? (
          <p>No trending data found.</p>
        ) : (
          trendingVestrData.map((item) => renderStockItem(item))
        )}
      </div>
    )
  }

  // Toplist (Charts tab)
  const renderActiveTopSection = () => {
    if (!activeTopData || !activeTopData.data) return null

    const page = activeTopData.page
    const totalPages = activeTopData.totalPages
    const data = activeTopData.data

    return (
      <div className="mv2-us-container">
        <div className="mv2-section-filters-wrapper" style={{ margin: 0 }}>
          <div className="mv2-section-filters">
            <button
              onClick={() => {
                setActiveTopPage(1)
                setActiveTopFilter('mostactive')
              }}
              className={`CreatePostFeedButton ${
                activeUsFilter === 'mostactive' && 'greenButton'
              }`}
            >
              Most active
            </button>
            <button
              onClick={() => {
                setActiveTopPage(1)
                setActiveTopFilter('marketcap')
              }}
              className={`CreatePostFeedButton ${
                activeUsFilter === 'marketcap' && 'greenButton'
              }`}
            >
              Mkt cap
            </button>
            <button
              onClick={() => {
                setActiveTopPage(1)
                setActiveTopFilter('trending')
              }}
              className={`CreatePostFeedButton ${
                activeUsFilter === 'trending' && 'greenButton'
              }`}
            >
              Trending
            </button>
          </div>
          <div className="mv2-pagination-arrows">
            <button
              className="mv2-pagination-button"
              onClick={() => setActiveTopPage((prev) => Math.max(prev - 1, 1))}
              disabled={activeTopPage === 1}
            >
              <FontAwesomeIcon
                icon={faArrowLeft}
                style={{
                  fontSize: '2rem',
                  color:
                    activeTopPage === 1
                      ? 'var(--border-color)'
                      : 'var(--text-color)',
                }}
              />
            </button>
            <button
              className="mv2-pagination-button"
              onClick={() =>
                setActiveTopPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={activeTopPage === totalPages}
            >
              <FontAwesomeIcon
                icon={faArrowRight}
                style={{
                  fontSize: '2rem',
                  color:
                    activeTopPage === totalPages
                      ? 'var(--border-color)'
                      : 'var(--text-color)',
                }}
              />
            </button>
          </div>
        </div>
        <div
          style={{
            minHeight: '80rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {activeTopLoading ? (
            <LoadingSpinner />
          ) : (
            <table className="mv2-table">
              <thead className="mv2-thead">
                <tr className="mv2-thead-tr">
                  <th></th>
                  <th>Symbol</th>
                  <th>Name</th>
                  <th>Price</th>
                  <th>1d change</th>
                  <th>Volume</th>
                  {activeTopType === 'stocks' && <th>Market cap</th>}
                  {activeTopType === 'stocks' && <th>P/E Ratio</th>}
                </tr>
              </thead>
              <tbody className="mv2-tbody">
                {data.map((item, index) => (
                  <tr
                    style={{ cursor: 'pointer' }}
                    key={index}
                    onClick={() =>
                      handleNavigateToAsset(navigate, item?.ticker)
                    }
                  >
                    <td className="mv2-table-rank">
                      {(page - 1) * data.length + (index + 1)}
                    </td>
                    <td>{formatModifiedTicker(item?.ticker) || '--'}</td>
                    <td>{item?.name || '--'}</td>
                    <td>
                      ${fc(item?.dailyChangeData?.currentPrice, 2) || '--'}
                    </td>
                    <td
                      style={{
                        color: getChangePercentageColor(
                          item?.dailyChangeData?.todaysPercentChange,
                          tc()
                        ),
                      }}
                    >
                      {item?.dailyChangeData?.todaysPercentChange !==
                      undefined ? (
                        <>
                          {item?.dailyChangeData?.todaysPercentChange >= 0
                            ? '↑'
                            : '↓'}
                          {fc(
                            item?.dailyChangeData?.todaysPercentChange,
                            2,
                            true
                          )}
                          %
                        </>
                      ) : (
                        '--'
                      )}
                    </td>
                    <td>
                      {formatNumber(item?.dailyChangeData?.todaysVolume) ||
                        '--'}
                    </td>
                    {activeTopType === 'stocks' && (
                      <td>{formatNumber(item?.marketCap) || '--'}</td>
                    )}
                    {activeTopType === 'stocks' && (
                      <td>{item?.peRatio || '--'}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    )
  }

  //
  // =============== USE EFFECTS ===============
  //

  // Key Indicators
  useEffect(() => {
    fetchKeyIndicators()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyIndicatorTime])

  // World Markets
  useEffect(() => {
    if (selectedExploreTab === 'overview') {
      fetchWorldMarkets()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorldMarketsType, worldMarketsPage, selectedExploreTab])

  // Forex
  useEffect(() => {
    if (selectedExploreTab === 'overview') {
      fetchForex()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedForexRegion, forexPage, selectedExploreTab])

  // Stocks
  useEffect(() => {
    if (selectedExploreTab === 'overview') {
      fetchStocksData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stocksMode, stocksPage, selectedExploreTab])

  // Crypto
  useEffect(() => {
    if (selectedExploreTab === 'overview') {
      fetchCryptoData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cryptoMode, cryptoPage, selectedExploreTab])

  // Trending on Vestr
  useEffect(() => {
    if (selectedExploreTab === 'overview') {
      fetchTrendingOnVestrData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendingVestrMode, trendingVestrPage, selectedExploreTab])

  // Toplist (Charts Tab)
  useEffect(() => {
    if (selectedExploreTab === 'charts') {
      fetchToplistData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTopType, activeUsFilter, activeTopPage, selectedExploreTab])

  // Watchlists
  useEffect(() => {
    fetchStockLists()
    fetchKeyIndicators()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken, isAuthenticated])

  // Active watchlist stocks
  useEffect(() => {
    if (activeWatchlist) {
      fetchActiveListStocks()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWatchlist, currentPage])

  //
  // =============== RENDER MAIN COMPONENT ===============
  //
  return (
    <div className="MarketsV2">
      {!isMobile && (
        <>
          {/* WATCHLISTS */}
          <div
            className="mv2-watchlists-wrapper"
            style={{
              marginTop: '2rem',
              paddingBottom: '3rem',
              borderBottom: 'var(--border)',
            }}
          >
            <div className="mv2-watchlists">
              <div className="mv2-watchlists-header">
                <h2 className="mv2-watchlists-h2">Watchlists</h2>
                <div className="mv2-watchlist-row">
                  {stockLists.map((list) => (
                    <div key={list._id} className="mv2-watchlist-item">
                      <div className="mv2-watchlist-btn-wrapper">
                        <button
                          className={`CreatePostFeedButton ${
                            activeWatchlist === list._id ? 'greenButton' : ''
                          }`}
                          onClick={() => {
                            setActiveWatchlist(list._id)
                            setCurrentPage(1)
                          }}
                        >
                          {list.name}
                        </button>
                        <button
                          className="keyword-x"
                          onClick={() => handleDeleteList(list._id)}
                        >
                          X
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div
                className="mv2-watchlist-col"
                style={{
                  justifyContent: 'end',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2rem',
                }}
              >
                {activeWatchlist &&
                  activeListStocks?.length > 0 &&
                  totalPages > 1 && (
                    <div className="mv2-pagination-arrows">
                      <button
                        className="mv2-pagination-button"
                        onClick={() =>
                          setCurrentPage((prev) => Math.max(prev - 1, 1))
                        }
                        disabled={currentPage === 1}
                      >
                        <FontAwesomeIcon
                          icon={faArrowLeft}
                          style={{
                            fontSize: '2rem',
                            color:
                              currentPage === 1
                                ? 'var(--border-color)'
                                : 'var(--text-color)',
                          }}
                        />
                      </button>
                      <button
                        className="mv2-pagination-button"
                        onClick={() =>
                          setCurrentPage((prev) =>
                            Math.min(prev + 1, totalPages)
                          )
                        }
                        disabled={currentPage === totalPages}
                      >
                        <FontAwesomeIcon
                          icon={faArrowRight}
                          style={{
                            fontSize: '2rem',
                            color:
                              currentPage === totalPages
                                ? 'var(--border-color)'
                                : 'var(--text-color)',
                          }}
                        />
                      </button>
                    </div>
                  )}

                {isCreatingNewList ? (
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <input
                      type="text"
                      value={newListName}
                      className="AddCashInput"
                      placeholder="Enter watchlist name"
                      onChange={(e) => setNewListName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCreateList()
                      }}
                    />
                    <button
                      className="CreatePostFeedButton"
                      onClick={handleCreateList}
                    >
                      Save
                    </button>
                    <button
                      className="CreatePostFeedButton"
                      onClick={() => setIsCreatingNewList(false)}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    className="CreatePostFeedButton"
                    onClick={handleCreateNewClick}
                  >
                    Create new +
                  </button>
                )}
              </div>
            </div>
            {renderActiveWatchListItems()}
          </div>
          {/* EXPLORE */}
          <div className="mv2-explore">
            <h2 className="mv2-watchlists-h2" style={{ marginTop: '2rem' }}>
              Explore
            </h2>
            <MarketSearchBar
              stock={stock}
              setStock={setStock}
              isAuthenticated={isAuthenticated}
              userToken={userToken}
              navigate={navigate}
              marginVertical="1.5rem"
            />

            {/* Explore Tab Buttons */}
            <div
              className="mv2-watchlist-row"
              style={{ marginBottom: '1.5rem' }}
            >
              <button
                className={`CreatePostFeedButton ${
                  selectedExploreTab === 'overview' && 'greenButton'
                }`}
                onClick={() => setSelectedExploreTab('overview')}
              >
                Overview
              </button>
              <button
                onClick={() => {
                  setActiveTopPage(1)
                  setActiveTopType('stocks')
                  setSelectedExploreTab('charts')
                }}
                className={`CreatePostFeedButton ${
                  activeTopType === 'stocks' &&
                  selectedExploreTab === 'charts' &&
                  'greenButton'
                }`}
              >
                Stocks
              </button>
              <button
                onClick={() => {
                  setActiveTopPage(1)
                  setActiveTopType('crypto')
                  setSelectedExploreTab('charts')
                }}
                className={`CreatePostFeedButton ${
                  activeTopType === 'crypto' &&
                  selectedExploreTab === 'charts' &&
                  'greenButton'
                }`}
              >
                Crypto
              </button>
            </div>

            {/* Render Overview or Charts */}
            {selectedExploreTab === 'overview' && (
              <>
                <div className="mv2-activelist-flex">
                  {renderKeyIndicators()}
                  {renderWorldMarketsSection()}
                  {renderStocksSection()}
                </div>
                <div className="mv2-activelist-flex">
                  {renderCryptoSection()}
                  {renderForexSection()}
                  {renderTrendingOnVestrSection()}
                </div>
              </>
            )}

            {selectedExploreTab === 'charts' && renderActiveTopSection()}
          </div>
        </>
      )}
      {isMobile && (
        <div className="mv2-explore">
          <h2 className="mv2-watchlists-h2" style={{ marginTop: '2rem' }}>
            Markets
          </h2>
          {/* <MarketSearchBar
            stock={stock}
            setStock={setStock}
            isAuthenticated={isAuthenticated}
            userToken={userToken}
            navigate={navigate}
            marginVertical="1.5rem"
          /> */}
          {/* Explore Tab Buttons */}
          {/* <div className="mv2-watchlist-row" style={{ marginBottom: '1.5rem' }}>
            <button
              className={`CreatePostFeedButton ${
                selectedExploreTab === 'overview' && 'greenButton'
              }`}
              onClick={() => setSelectedExploreTab('overview')}
            >
              Overview
            </button>
            <button
              onClick={() => {
                setActiveTopPage(1)
                setActiveTopType('stocks')
                setSelectedExploreTab('charts')
              }}
              className={`CreatePostFeedButton ${
                activeTopType === 'stocks' &&
                selectedExploreTab === 'charts' &&
                'greenButton'
              }`}
            >
              Stocks
            </button>
            <button
              onClick={() => {
                setActiveTopPage(1)
                setActiveTopType('crypto')
                setSelectedExploreTab('charts')
              }}
              className={`CreatePostFeedButton ${
                activeTopType === 'crypto' &&
                selectedExploreTab === 'charts' &&
                'greenButton'
              }`}
            >
              Crypto
            </button>
          </div> */}

          {/* Render Overview or Charts */}
          {selectedExploreTab === 'overview' ? (
            <>
              <div className="mv2-activelist-flex">
                {renderKeyIndicators()}
                <div>
                  <h2
                    className="mv2-watchlists-h2"
                    style={{ marginTop: '2rem' }}
                  >
                    Markets
                  </h2>
                </div>
                <div className="LeadBestButtonsWrapper">
                  <div className="LeadBestButtons">
                    {mobileMarketWatchOptions.map((t) => (
                      <button
                        key={t.value}
                        className={`CreatePostFeedButton ${
                          selectedSection === t.value ? 'greenButton' : ''
                        }`}
                        onClick={() => {
                          setSelectedSection(t.value)
                        }}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mv2-activelist-flex">
                {selectedSection === 'Indices' && renderWorldMarketsSection()}
                {selectedSection === 'Stocks' && renderStocksSection()}
                {selectedSection === 'Crypto' && renderCryptoSection()}
                {selectedSection === 'Forex' && renderForexSection()}
                {selectedSection === 'Trending' &&
                  renderTrendingOnVestrSection()}
              </div>
            </>
          ) : (
            renderActiveTopSection()
          )}
        </div>
      )}
    </div>
  )
}
